import { useLayoutEffect, useRef } from 'react'

/** 정렬이 끝나면 그림 격자에 쏘는 사건 — 겹침 그리퍼(`SheetGripper`)가 자리를 다시 잰다(옮김은 크기 변화가 아니라 관찰자가 못 본다) */
export const RELAYOUT_EVENT = 'eqsheet:relayout'

/**
 * **장치 묶음을 트레이 가운데에 맞춘다** — 장치 그림의 묶음(TC 랙 · 압착 · 스테이지 · 폐기통 · 이름표)의 세로 가운데가 작업 전 트레이 몸체의
 * 세로 가운데와 같은 높이에 서게 장치 칸을 위아래로 옮긴다(eric 2026-10-15 「가운데 정렬해서 트레이 높이에 맞춰」 — 10-12 의 바닥선 맞춤을 바꿨다).
 *
 * 🔴 **그려진 것에서 잰다** — 두 칸은 배율이 저마다라(칸 맞춤 `--fit-zoom` × 그림 배율) CSS 숫자로는 못 맞춘다. 트레이 몸체(`.fig-tray__body`)와
 *    장치 묶음(장치 칸 그림의 `[data-part]` 합집합 · 상태 글자 `status` 는 뺀다)의 화면 가운데 차이만큼 장치 칸 안 그림(`.eqsheet__fit`)에
 *    `translate` 를 건다. 칸 크기 · 배율이 바뀌면 다시 잰다.
 * 그림의 배율(판 맞춤 `zoom`)은 «÷»로 되돌린다 — 화면 px 를 칸의 CSS px 로.
 * 둘 중 하나라도 없으면(트레이 칸이 없는 장비 · 그림 전) 옮기지 않는다.
 */
/** 장치 칸 그림 묶음의 화면 세로 범위 — `[data-part]` 합집합(상태 글자 · 크기 없는 것 제외). 없으면 `undefined` */
function groupBox(cell: Element): { top: number; bottom: number } | undefined {
  let top = Infinity
  let bottom = -Infinity
  for (const part of cell.querySelectorAll('svg [data-part]:not([data-part="status"])')) {
    const box = part.getBoundingClientRect()
    if (box.height === 0) continue
    top = Math.min(top, box.top)
    bottom = Math.max(bottom, box.bottom)
  }
  return top === Infinity ? undefined : { top, bottom }
}

export function useAlignDeckLine<T extends HTMLElement>(enabled: boolean) {
  const ref = useRef<T>(null)
  useLayoutEffect(() => {
    const figs = ref.current
    if (!enabled || figs === null || typeof ResizeObserver === 'undefined') return
    const align = () => {
      const cell = figs.querySelector<HTMLElement>('.eqsheet__cell--dev')
      if (cell === null) return
      // 칸 전체를 옮기면 투명한 클릭 영역이 위쪽 장비 탭을 덮는다. 칸은 고정하고 그림만 옮긴다.
      const drawing = cell.querySelector<HTMLElement>('.eqsheet__fit')
      if (drawing === null) return
      const tray = figs.querySelector('.eqsheet__cell--pre .fig-tray__body')
      const group = groupBox(cell)
      if (tray === null || group === undefined) return
      /*
       * 🔴 **나란히 설 때만 맞춘다.** 좁은 카드(`@container eqcard (width < 720px)`)는 두 줄(장비 / 작업 전 · 작업 후)로 접혀 작업 전 칸이
       *    장치 칸 «아래 줄»에 선다 — 그때 높이를 맞추면 장치 칸이 아래 줄까지 내려가 트레이 위에 겹친다(1920 실측 · 장치 칸 +272px ·
       *    「TC 랙」 · 「폐기통」이 「칸 배정 정보 없음」과 겹치고 압착 · UV 그림이 트레이 사이로 눌려 보였다 · 2026-10-13).
       *    줄은 배치 위치(`offsetTop` — `translate` 를 타지 않는다)로 가린다. 접혔으면 옮긴 것을 되돌린다.
       */
      const pre = tray.closest<HTMLElement>('.eqsheet__cell--pre')
      /* 위아래 어느 쪽으로 접혀도(모듈 앱은 트레이가 위 · eric 2026-10-18) 다른 줄이면 맞추지 않는다 */
      const folded =
        pre !== null &&
        (pre.offsetTop >= cell.offsetTop + cell.offsetHeight ||
          cell.offsetTop >= pre.offsetTop + pre.offsetHeight)
      if (folded) {
        if (drawing.dataset['deckDy'] === undefined) return
        drawing.style.translate = ''
        delete drawing.dataset['deckDy']
        figs.dispatchEvent(new Event(RELAYOUT_EVENT))
        return
      }
      const box = drawing.getBoundingClientRect()
      if (box.width === 0 || drawing.offsetWidth === 0) return
      const zoom = box.width / drawing.offsetWidth || 1
      const now = Number(drawing.dataset['deckDy'] ?? 0)
      const body = tray.getBoundingClientRect()
      const gap = ((body.top + body.bottom) / 2 - (group.top + group.bottom) / 2) / zoom
      const next = Math.round(now + gap)
      if (Math.abs(next - now) < 1) return
      drawing.dataset['deckDy'] = String(next)
      drawing.style.translate = `0 ${String(next)}px`
      figs.dispatchEvent(new Event(RELAYOUT_EVENT))
    }
    align()
    const observer = new ResizeObserver(align)
    observer.observe(figs)
    for (const cell of figs.querySelectorAll('.eqsheet__cell')) observer.observe(cell)
    for (const fit of figs.querySelectorAll('.eqsheet__fit')) observer.observe(fit)
    return () => {
      observer.disconnect()
      const drawing = figs.querySelector<HTMLElement>('.eqsheet__cell--dev .eqsheet__fit')
      if (drawing !== null) {
        drawing.style.translate = ''
        delete drawing.dataset['deckDy']
      }
    }
  }, [enabled])
  return ref
}
